import * as React from "react";
import { Modal, notification } from "antd";
import { observer } from "mobx-react-lite";
import { CopyOutlined, DownloadOutlined } from "@ant-design/icons";
import { NGButton } from "../../../newcomponents/button/Button";
import { Agent } from "../../../schema/types/agent";
import { CodeEditor } from "../../group/identityMatcher/codeEditor";
import { CustomScrollbars } from "../../../components/Scrollbar";
import { ConsoleContext } from "../../../mobxStores/consoleContext/consoleContext";
import { AgentScriptTabs } from "../agentScriptTabs";

interface Props {
  agent: Agent;
  onDone: any;
  visible: boolean;
}

const BootstrapConfigModalRaw: React.FC<Props> = ({ agent, onDone, visible }) => {
  const protocolVersion = agent.status?.protocolVersion || "v1";
  const bootstrapConfig = agent.status?.bootstrapConfig;
  const bootstrapConfigJson = JSON.stringify(bootstrapConfig, null, 2);

  const [tookAction, setTookAction] = React.useState(false);
  const [step, setStep] = React.useState(1);

  async function copyToClipboard(text: string, message: string = "Copied to clipboard") {
    navigator.clipboard.writeText(text);
    notification.success({ message });
    setTookAction(true);
  }

  function downloadFile(content: string, filename: string) {
    const file = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setTookAction(true);
  }

  function localOnDone() {
    const link = agent.links.find((l: any) => l.rel === "self")!.href;
    onDone(link);
  }

  // v1: Single step with bootstrap config
  if (protocolVersion === "v1") {
    return (
      <Modal
        title={"Agent Bootstrap Config"}
        open={visible}
        closable={false}
        destroyOnClose
        width={700}
        footer={
          <div className="flex items-center justify-end gap-4">
            <NGButton
              variant="secondary"
              iconPosition="left"
              renderIcon={(hover, props) => <DownloadOutlined {...props} />}
              onClick={() => downloadFile(bootstrapConfigJson, `${agent.name}-bootstrapConfig.json`)}
            >
              Download Config File
            </NGButton>
            <NGButton variant="primary" onClick={localOnDone} disabled={!tookAction}>
              Done
            </NGButton>
          </div>
        }
      >
        <div>
          <div>Agent bootstrap config created.</div>
          <div className="mb-4">
            Copy or download the contents, you will not have access to it after you close this screen.
          </div>
          <div className="flex items-center gap-2 mb-2">
            <NGButton
              variant="secondary"
              size="small"
              iconPosition="left"
              renderIcon={(hover, props) => <CopyOutlined {...props} />}
              onClick={() => copyToClipboard(bootstrapConfigJson)}
            >
              Copy
            </NGButton>
          </div>
          <CodeEditor
            value={bootstrapConfigJson}
            setValue={() => {}}
            language="json"
            options={{
              basicSetup: { lineNumbers: true, highlightActiveLine: false, autocompletion: false, foldGutter: false },
              readOnly: true,
            }}
          />
        </div>
      </Modal>
    );
  }

  // v2: Two-step flow
  return (
    <Modal
      title={step === 1 ? "Agent Bootstrap Config" : "Download Scripts"}
      open={visible}
      closable={false}
      destroyOnClose
      styles={{
        body: { height: "70vh", display: "flex", flexDirection: "column" },
        content: { minWidth: 600, maxWidth: "90vw" },
      }}
      width={1200}
      footer={
        <div className="flex items-center justify-between">
          <div>{step === 2 && <NGButton variant="secondary" onClick={() => setStep(1)}>Back</NGButton>}</div>
          <div className="flex items-center gap-4">
            {step === 1 ? (
              <>
                <NGButton
                  variant="secondary"
                  onClick={() => downloadFile(bootstrapConfigJson, `${agent.name}-bootstrapConfig.json`)}
                >
                  <DownloadOutlined className="mr-1" />
                  Download Config File
                </NGButton>
                <NGButton
                  variant="primary"
                  onClick={() => {
                    setTookAction(true);
                    setStep(2);
                  }}
                >
                  Next
                </NGButton>
              </>
            ) : (
              <NGButton variant="primary" onClick={localOnDone} disabled={!tookAction}>
                Done
              </NGButton>
            )}
          </div>
        </div>
      }
    >
      {step === 1 ? (
        <div className="flex flex-col flex-1 p-4">
          <div>Agent bootstrap config created.</div>
          <div className="mb-4">
            Copy or download the contents, you will not have access to it after you close this screen.
          </div>
          <div className="flex items-center gap-2 mb-2">
            <NGButton
              variant="secondary"
              size="small"
              iconPosition="left"
              renderIcon={(hover, props) => <CopyOutlined {...props} />}
              onClick={() => copyToClipboard(bootstrapConfigJson)}
            >
              Copy
            </NGButton>
          </div>
          <CustomScrollbars className="flex-1 border rounded" style={{ minHeight: 0 }}>
            <CodeEditor
              value={bootstrapConfigJson}
              setValue={() => {}}
              language="json"
              options={{
                basicSetup: { lineNumbers: true, highlightActiveLine: false, autocompletion: false, foldGutter: false },
                readOnly: true,
              }}
            />
          </CustomScrollbars>
        </div>
      ) : (
        <div className="h-full p-4">
          <AgentScriptTabs
            org={ConsoleContext.org!}
            agentName={agent.name}
            bootstrapConfig={bootstrapConfig!}
            onAction={() => setTookAction(true)}
          />
        </div>
      )}
    </Modal>
  );
};

export const BootstrapConfigModal = observer(BootstrapConfigModalRaw);
